import { type ReactNode, useEffect, useRef } from "react";
import type { FieldError, FieldValues, UseFormRegisterReturn, UseFormWatch } from "react-hook-form";

/**
 * Form pieces for the Decisions tools, in the prototype's form style (`.act-body .field`). Values
 * stay strings in the form (what the user typed) and are parsed on submit, so a half-typed amount
 * never jumps around.
 */

// ------------------------------------------------------------------ zod helpers

// Shared with AbiChat's simulation tools (plan-logic/decisions/values).
export {
  amount,
  optAmount,
  zAmount,
  zDate,
  zOptionalAmount,
  zOptionalDate,
  zPositive,
  zRate,
} from "@/plan-logic/decisions/values";

// ------------------------------------------------------------------ components

export function Field({
  label,
  error,
  hint,
  children,
  htmlFor,
}: {
  label: ReactNode;
  error?: FieldError | { message?: string };
  hint?: ReactNode;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error?.message ? (
        <p className="dz-err" role="alert">
          {error.message}
        </p>
      ) : (
        hint && <p className="hint">{hint}</p>
      )}
    </div>
  );
}

export function MoneyInput({
  id,
  reg,
  placeholder = "0",
}: {
  id: string;
  reg: UseFormRegisterReturn;
  placeholder?: string;
}) {
  return (
    <div className="pre">
      <input id={id} type="text" inputMode="decimal" placeholder={placeholder} {...reg} />
    </div>
  );
}

export function TextInput({
  id,
  reg,
  placeholder,
  inputMode,
}: {
  id: string;
  reg: UseFormRegisterReturn;
  placeholder?: string;
  inputMode?: "decimal" | "numeric" | "text";
}) {
  return <input id={id} type="text" inputMode={inputMode} placeholder={placeholder} {...reg} />;
}

export function DateInput({ id, reg }: { id: string; reg: UseFormRegisterReturn }) {
  return <input id={id} type="date" {...reg} />;
}

/**
 * The Analyze button under a tool's form (v43: nothing runs until it is pressed). "Analyze again"
 * once a result is showing for these inputs or an earlier run of them.
 */
export function RunButton({
  busy,
  ran = false,
  label,
  note,
}: {
  busy: boolean;
  ran?: boolean;
  label?: string;
  note?: ReactNode;
}) {
  return (
    <div className="decrun">
      <button type="submit" className="btn p dz-run" disabled={busy} data-testid="decision-run">
        {busy ? "Analyzing…" : (label ?? (ran ? "Analyze again" : "Analyze"))}
      </button>
      {note && <p className="hint">{note}</p>}
    </div>
  );
}

/**
 * Calls `onEdit` whenever a value in the form changes (typing, a select, or a button that sets a
 * value), so the page can put the last answer back behind the Analyze button (v43).
 */
export function useEdits<T extends FieldValues>(
  watch: UseFormWatch<T>,
  onEdit: (() => void) | undefined,
) {
  const ref = useRef(onEdit);
  useEffect(() => {
    ref.current = onEdit;
  }, [onEdit]);
  useEffect(() => {
    const sub = watch(() => ref.current?.());
    return () => sub.unsubscribe();
  }, [watch]);
}
